import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, Trash2, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Shell = ({ children }) => (
  <div className="min-h-screen relative overflow-hidden">
    <div
      className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-rose-950"
      aria-hidden="true"
    />
    <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md backdrop-blur-xl bg-black/30 border border-white/20 shadow-2xl rounded-3xl">
        <CardContent className="py-10 px-6 sm:px-10">{children}</CardContent>
      </Card>
    </div>
  </div>
);

// --- Request deletion (public, no login) ---
export const DeleteAccountRequest = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!acknowledged) {
      setError('Please confirm you understand this is permanent.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/account/deletion-request`, { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Shell>
        <div className="text-center" data-testid="delete-account-request-success">
          <Mail className="h-16 w-16 text-pink-400 mx-auto mb-5" />
          <h1 className="text-2xl font-bold text-white mb-3">Check your email</h1>
          <p className="text-gray-300 text-sm leading-relaxed mb-6">
            If an account exists for <span className="text-white font-medium">{email}</span>,
            we&apos;ve sent a confirmation link. Open it from your inbox within 24 hours
            to permanently delete your account.
          </p>
          <p className="text-xs text-gray-400 mb-8">
            Don&apos;t see it? Check your spam folder, or email{' '}
            <a className="text-pink-400 underline" href="mailto:support@shotclockdating.com">
              support@shotclockdating.com
            </a>.
          </p>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-white/30 text-white hover:bg-white/10"
            data-testid="delete-account-back-home"
          >
            Back to home
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-red-500/15 border border-red-400/30 mb-4">
          <Trash2 className="h-7 w-7 text-red-400" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Delete your account</h1>
        <p className="text-sm text-gray-300 leading-relaxed">
          Enter the email you signed up with. We&apos;ll send a confirmation link
          to permanently delete your Shot Clock Dating account and all associated data.
        </p>
      </div>

      <div className="rounded-xl bg-amber-500/10 border border-amber-400/30 p-4 mb-6 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-300 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-100 leading-relaxed">
          <strong className="block mb-1">This action is permanent.</strong>
          Your profile, matches, messages, speed-date history, subscription records,
          and credits will be deleted and cannot be recovered.
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="delete-account-form">
        <div>
          <label className="text-xs text-gray-300 mb-2 block" htmlFor="delete-email">
            Account email
          </label>
          <Input
            id="delete-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            data-testid="delete-account-email-input"
          />
        </div>

        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-white/40 bg-white/10 accent-rose-500"
            data-testid="delete-account-acknowledge-checkbox"
          />
          <span className="text-xs text-gray-200 leading-relaxed">
            I understand my account and all data will be permanently deleted and cannot be restored.
          </span>
        </label>

        {error && (
          <div
            className="rounded-lg bg-red-500/15 border border-red-400/30 px-4 py-3 text-sm text-red-200"
            data-testid="delete-account-error"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white font-semibold py-6 rounded-xl shadow-lg"
          data-testid="delete-account-submit-button"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Sending confirmation…
            </>
          ) : (
            'Send deletion confirmation email'
          )}
        </Button>
      </form>

      <p className="text-xs text-gray-400 text-center mt-6">
        Prefer to keep your account? <a href="/" className="text-pink-400 underline">Go back</a>.
      </p>
    </Shell>
  );
};

// --- Confirm deletion (link from email) ---
export const DeleteAccountConfirm = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [state, setState] = useState('idle'); // idle | loading | success | error | expired
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage('This link is missing a deletion token. Please use the link from your email.');
    }
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;
    setState('loading');
    try {
      const res = await axios.post(`${API}/account/deletion-confirm`, { token });
      setState('success');
      setMessage(res.data?.message || 'Your account has been permanently deleted.');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Deletion failed. Please try again.';
      setState(status === 410 ? 'expired' : 'error');
      setMessage(detail);
    }
  };

  if (state === 'success') {
    return (
      <Shell>
        <div className="text-center" data-testid="delete-account-confirm-success">
          <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-5" />
          <h1 className="text-2xl font-bold text-white mb-3">Account deleted</h1>
          <p className="text-gray-300 text-sm leading-relaxed mb-8">{message}</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600"
            data-testid="delete-account-confirm-done"
          >
            Done
          </Button>
        </div>
      </Shell>
    );
  }

  if (state === 'error' || state === 'expired') {
    return (
      <Shell>
        <div className="text-center" data-testid="delete-account-confirm-error">
          <AlertTriangle className="h-16 w-16 text-amber-400 mx-auto mb-5" />
          <h1 className="text-2xl font-bold text-white mb-3">
            {state === 'expired' ? 'Link expired' : 'Something went wrong'}
          </h1>
          <p className="text-gray-300 text-sm leading-relaxed mb-8">{message}</p>
          <Button
            onClick={() => navigate('/delete-account')}
            className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600"
            data-testid="delete-account-confirm-retry"
          >
            Request a new link
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="text-center mb-6">
        <Trash2 className="h-14 w-14 text-red-400 mx-auto mb-5" />
        <h1 className="text-2xl font-bold text-white mb-3">Confirm account deletion</h1>
        <p className="text-sm text-gray-300 leading-relaxed">
          You&apos;re about to permanently delete your Shot Clock Dating account and all associated data.
          This cannot be undone.
        </p>
      </div>

      <ul className="text-xs text-gray-300 space-y-2 mb-6 bg-white/5 rounded-xl border border-white/10 p-4">
        <li>• Profile, photos, bio, and preferences</li>
        <li>• All matches and conversations</li>
        <li>• Speed-date session history</li>
        <li>• Subscription records and remaining credits</li>
      </ul>

      <div className="space-y-3">
        <Button
          onClick={handleConfirm}
          disabled={state === 'loading' || !token}
          className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white font-semibold py-6 rounded-xl"
          data-testid="delete-account-confirm-button"
        >
          {state === 'loading' ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Deleting account…
            </>
          ) : (
            'Yes, permanently delete my account'
          )}
        </Button>
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="w-full border-white/30 text-white hover:bg-white/10 py-6 rounded-xl"
          data-testid="delete-account-cancel-button"
        >
          Cancel
        </Button>
      </div>
    </Shell>
  );
};

export default DeleteAccountRequest;
